/**
 * Recipes - the bridge between what is sold and what is stocked.
 *
 * Without a recipe, a menu item sells without consuming anything and the
 * inventory quietly stops meaning anything. So this screen leads with the
 * items that have no recipe yet.
 *
 * The cost figure is the other reason to be here: it turns a menu price into a
 * margin, and shows the owner which dishes are actually worth selling.
 */

import { useState, useCallback, useEffect } from 'react';
import { FiPlus, FiSearch, FiClipboard, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';

import { inventoryApi, menuApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, Badge, EmptyState } from '../components/ui/Feedback';
import { Field, NumberInput, Select } from '../components/ui/Field';
import { money, quantity } from '../utils/format';

const RecipesPage = () => {
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null);

    const loadItems = useCallback(() => menuApi.listItems({ search }), [search]);
    const { data: menuItems, loading, refetch } = useAsync(loadItems, [search]);

    const loadRecipes = useCallback(() => inventoryApi.listRecipes(), []);
    const { data: recipes, refetch: refetchRecipes } = useAsync(loadRecipes, []);

    const loadStock = useCallback(() => inventoryApi.listItems({}), []);
    const { data: stockItems } = useAsync(loadStock, []);

    // How many ingredients each menu item has, and what they cost.
    const recipeSummary = (recipes || []).reduce((acc, row) => {
        const entry = acc[row.Menu_Item_ID] || { count: 0, cost: 0 };
        entry.count += 1;
        entry.cost += Number(row.Line_Cost) || 0;
        acc[row.Menu_Item_ID] = entry;
        return acc;
    }, {});

    const columns = [
        {
            key: 'Item_Name',
            header: 'Menu item',
            render: (row) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{row.Item_Name}</p>
                    <p className="truncate text-xs text-slate-500">{row.Category_Name}</p>
                </div>
            )
        },
        {
            key: 'ingredients',
            header: 'Ingredients',
            render: (row) => {
                const summary = recipeSummary[row.Menu_Item_ID];
                if (!summary) {
                    return (
                        <span className="flex items-center gap-1 text-xs text-amber-600">
                            <FiAlertTriangle className="h-3.5 w-3.5" />
                            no recipe — sells without using stock
                        </span>
                    );
                }
                return <Badge tone="green">{summary.count} ingredient(s)</Badge>;
            }
        },
        {
            key: 'Sale_Price',
            header: 'Price',
            align: 'right',
            render: (row) => money(row.Sale_Price)
        },
        {
            key: 'cost',
            header: 'Food cost',
            align: 'right',
            render: (row) => {
                const summary = recipeSummary[row.Menu_Item_ID];
                return summary ? money(summary.cost) : '—';
            }
        },
        {
            key: 'margin',
            header: 'Margin',
            align: 'right',
            render: (row) => {
                const summary = recipeSummary[row.Menu_Item_ID];
                if (!summary) return '—';

                // Against the tax-exclusive value, since the tax is not the
                // restaurant's money.
                const net = Number(row.Sale_Price) / (1 + (Number(row.Sales_Tax_Rate_NUM) || 0) / 100);
                const margin = net - summary.cost;
                const percent = net > 0 ? (margin / net) * 100 : 0;

                return (
                    <div>
                        <p className={`font-medium ${margin > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            {money(margin)}
                        </p>
                        <p className="text-xs text-slate-500">{percent.toFixed(0)}%</p>
                    </div>
                );
            }
        },
        {
            key: 'actions',
            header: '',
            render: (row) => (
                <div className="flex justify-end">
                    <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={(event) => { event.stopPropagation(); setEditing(row); }}
                    >
                        {recipeSummary[row.Menu_Item_ID] ? 'Edit recipe' : 'Add recipe'}
                    </button>
                </div>
            )
        }
    ];

    const withoutRecipe = (menuItems || []).filter(
        (item) => item.Tracks_Inventory && !recipeSummary[item.Menu_Item_ID]
    ).length;

    return (
        <div className="page">
            <PageHeader
                title="Recipes"
                subtitle="What each dish takes out of the store room"
            />

            {withoutRecipe > 0 && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 py-1 px-3 text-sm text-amber-900">
                    <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>
                        <strong>{withoutRecipe}</strong> item(s) have no recipe. They sell normally, but
                        nothing is deducted from stock when they do — so the inventory figures will
                        drift away from reality.
                    </p>
                </div>
            )}

            <div className="relative w-full sm:max-w-md">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                    className="input pl-9"
                    placeholder="Search menu items"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>

            <DataTable
                className="min-h-0 flex-1"
                columns={columns}
                rows={menuItems || []}
                loading={loading}
                rowKey={(row) => row.Menu_Item_ID}
                onRowClick={setEditing}
                emptyTitle="No menu items"
                emptyIcon={FiClipboard}
            />

            <RecipeEditor
                menuItem={editing}
                stockItems={stockItems || []}
                onClose={() => setEditing(null)}
                onSaved={() => { refetch(); refetchRecipes(); }}
            />
        </div>
    );
};

/* ------------------------------- the editor ------------------------------- */

const RecipeEditor = ({ menuItem, stockItems, onClose, onSaved }) => {
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!menuItem?.Menu_Item_ID) {
            setLines([]);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        inventoryApi.getRecipe(menuItem.Menu_Item_ID)
            .then((result) => {
                if (cancelled) return;
                setLines((result.lines || []).map((line) => ({
                    stockItemId: line.Stock_Item_ID,
                    quantity: Number(line.Quantity),
                    wastagePercent: Number(line.Wastage_Percent) || 0
                })));
            })
            .catch((err) => !cancelled && toast.error(err.message))
            .finally(() => !cancelled && setLoading(false));

        return () => { cancelled = true; };
    }, [menuItem]);

    const { run: save, busy } = useAction(
        () => inventoryApi.setRecipe(
            menuItem.Menu_Item_ID,
            lines
                .filter((line) => line.stockItemId && Number(line.quantity) > 0)
                .map((line) => ({
                    stockItemId: Number(line.stockItemId),
                    quantity: Number(line.quantity),
                    wastagePercent: Number(line.wastagePercent) || 0
                }))
        ),
        { successMessage: 'Recipe saved.', onSuccess: () => { onSaved(); onClose(); } }
    );

    // The running cost, so the effect of a change is visible as it is made.
    const totalCost = lines.reduce((sum, line) => {
        const stock = stockItems.find((s) => String(s.Stock_Item_ID) === String(line.stockItemId));
        if (!stock) return sum;
        const withWastage = (Number(line.quantity) || 0) * (1 + (Number(line.wastagePercent) || 0) / 100);
        return sum + withWastage * Number(stock.Average_Cost || 0);
    }, 0);

    const salePrice = Number(menuItem?.Sale_Price) || 0;
    const netPrice = salePrice / (1 + (Number(menuItem?.Sales_Tax_Rate_NUM) || 0) / 100);

    return (
        <Modal
            open={!!menuItem}
            onClose={onClose}
            title={`Recipe for ${menuItem?.Item_Name || ''}`}
            subtitle={`Per one ${menuItem?.Sale_UOM || 'unit'} sold`}
            width="lg"
            dismissible={!busy}
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button type="button" className="btn-primary" onClick={() => save()} disabled={busy}>
                        {busy ? 'Saving…' : 'Save recipe'}
                    </button>
                </>
            )}
        >
            {loading ? (
                <p className="py-8 text-center text-sm text-slate-500">Loading the recipe…</p>
            ) : (
                <div className="space-y-3">
                    {lines.length === 0 && (
                        <EmptyState
                            title="No ingredients yet"
                            description="Add what one portion of this dish consumes."
                            icon={FiClipboard}
                        />
                    )}

                    {lines.map((line, index) => {
                        const stock = stockItems.find(
                            (s) => String(s.Stock_Item_ID) === String(line.stockItemId)
                        );

                        return (
                            <div key={index} className="flex items-end gap-2">
                                <Field label={index === 0 ? 'Ingredient' : null} className="flex-1">
                                    <Select
                                        value={line.stockItemId}
                                        onChange={(e) => setLines(lines.map((l, i) =>
                                            (i === index ? { ...l, stockItemId: e.target.value } : l)))}
                                    >
                                        <option value="">Choose…</option>
                                        {stockItems.map((s) => (
                                            <option key={s.Stock_Item_ID} value={s.Stock_Item_ID}>
                                                {s.Stock_Item_Name} ({s.Stock_Unit})
                                            </option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label={index === 0 ? 'Quantity' : null} className="w-28">
                                    <NumberInput
                                        step="0.0001"
                                        value={line.quantity}
                                        onChange={(e) => setLines(lines.map((l, i) =>
                                            (i === index ? { ...l, quantity: e.target.value } : l)))}
                                    />
                                </Field>

                                <div className="w-14 pb-2.5 text-xs text-slate-500">
                                    {stock?.Stock_Unit || ''}
                                </div>

                                <Field
                                    label={index === 0 ? 'Waste %' : null}
                                    className="w-24"
                                >
                                    <NumberInput
                                        value={line.wastagePercent}
                                        onChange={(e) => setLines(lines.map((l, i) =>
                                            (i === index ? { ...l, wastagePercent: e.target.value } : l)))}
                                    />
                                </Field>

                                <div className="w-24 pb-2.5 text-right text-sm">
                                    {stock ? (
                                        <span className="tabular text-slate-700">
                                            {money(
                                                (Number(line.quantity) || 0)
                                                * (1 + (Number(line.wastagePercent) || 0) / 100)
                                                * Number(stock.Average_Cost || 0)
                                            )}
                                        </span>
                                    ) : '—'}
                                </div>

                                <button
                                    type="button"
                                    className="btn-ghost btn-sm mb-1 !px-2 text-red-600"
                                    onClick={() => setLines(lines.filter((_, i) => i !== index))}
                                >
                                    <FiTrash2 className="h-4 w-4" />
                                </button>
                            </div>
                        );
                    })}

                    <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => setLines([...lines, { stockItemId: '', quantity: '', wastagePercent: 0 }])}
                    >
                        <FiPlus className="h-3.5 w-3.5" /> Add an ingredient
                    </button>

                    {/* The numbers this screen exists to produce */}
                    <dl className="rounded-xl bg-slate-50 p-3 text-sm">
                        <div className="flex justify-between py-0.5">
                            <dt className="text-slate-600">Menu price</dt>
                            <dd className="tabular">{money(salePrice)}</dd>
                        </div>
                        <div className="flex justify-between py-0.5">
                            <dt className="text-slate-600">Less sales tax</dt>
                            <dd className="tabular">{money(netPrice)}</dd>
                        </div>
                        <div className="flex justify-between py-0.5">
                            <dt className="text-slate-600">Food cost</dt>
                            <dd className="tabular">{money(totalCost)}</dd>
                        </div>
                        <div className="mt-1 flex justify-between border-t border-slate-200 pt-1.5">
                            <dt className="font-semibold text-slate-700">Margin</dt>
                            <dd className={`tabular font-bold
                                            ${netPrice - totalCost > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                                {money(netPrice - totalCost)}
                                {netPrice > 0 && (
                                    <span className="ml-2 text-xs font-normal">
                                        ({(((netPrice - totalCost) / netPrice) * 100).toFixed(0)}%)
                                    </span>
                                )}
                            </dd>
                        </div>
                    </dl>

                    <p className="text-xs text-slate-500">
                        Waste % covers trim and peel loss - 100g of onion on the plate may need 115g
                        out of the store.
                    </p>
                </div>
            )}
        </Modal>
    );
};

export default RecipesPage;
